import { useSyncExternalStore } from "react";

// Tracks the wall-clock time of the most recent Fast Refresh, app-wide.
//
// On every Fast Refresh (regardless of which file changed) React Native calls
// `global.__ReactRefresh.performReactRefresh()` — see RN's setUpReactRefresh.js,
// which runs `ReactRefreshRuntime.performReactRefresh()` then
// `DevSettings.onFastRefresh()`. We wrap that single global entry point once so any
// hot reload bumps the timestamp and notifies React subscribers.
//
// Dev-only. In production `__DEV__` is false, `useLastHotReload` returns `null`, and
// no global is touched.

type Listener = () => void;

type ReactRefreshRuntime = {
  performReactRefresh?: (...args: unknown[]) => unknown;
};

type MetroGlobalThis = typeof globalThis & {
  __METRO_GLOBAL_PREFIX__?: string;
};

function readReactRefreshRuntime(): ReactRefreshRuntime | null {
  const metroGlobal = globalThis as MetroGlobalThis;
  const key = `${metroGlobal.__METRO_GLOBAL_PREFIX__ ?? ""}__ReactRefresh`;
  const candidate: unknown = Reflect.get(globalThis, key);
  if (candidate == null || typeof candidate !== "object") {
    return null;
  }
  return candidate as ReactRefreshRuntime;
}

let lastHotReload: string | null = null;
const listeners = new Set<Listener>();
let installed = false;

function stamp() {
  lastHotReload = new Date().toLocaleTimeString("en-US", { hour12: false });
  for (const listener of listeners) {
    listener();
  }
}

function installRefreshHook() {
  if (installed || !__DEV__) {
    return;
  }
  installed = true;

  const refresh = readReactRefreshRuntime();
  // Refresh runtime not present (e.g. release-style bundle in dev) — leave the
  // build-time bundle stamp as the only signal.
  if (refresh == null || typeof refresh.performReactRefresh !== "function") {
    return;
  }

  const original = refresh.performReactRefresh.bind(refresh);
  refresh.performReactRefresh = (...args: unknown[]) => {
    const result = original(...args);
    stamp();
    return result;
  };
}

function subscribe(listener: Listener): () => void {
  installRefreshHook();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string | null {
  return lastHotReload;
}

/**
 * Returns the time of the last app-wide Fast Refresh as a formatted string, or
 * `null` if no hot reload has happened yet this session (or in production).
 */
export function useLastHotReload(): string | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
